import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  Search,
  ClipboardList,
  Ban,
  Info,
} from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";

interface AuditSelectProps {
  value: Id<"audits"> | null;
  onChange: (id: Id<"audits"> | null) => void;
  explicitNoAudit: boolean;
  onNoAuditChange: (value: boolean) => void;
  /** When true, visually highlights the selector to draw user attention */
  highlight?: boolean;
  /** Ref to programmatically open the popover */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

interface AuditOption {
  id: Id<"audits">;
  displayName: string;
  code: string;
}

const ACTIVE_STATUSES = ["pending", "in_progress"];

/**
 * Single-select dropdown for choosing which audit to scope document uploads to.
 * Shown in documents-list mode (not audit-view mode).
 * No selection + no explicit "no audit" = user must choose before uploading.
 */
export function AuditMultiSelect({
  value,
  onChange,
  explicitNoAudit,
  onNoAuditChange,
  highlight,
  triggerRef,
}: AuditSelectProps) {
  const { t } = useTranslation("documents");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch active audits using the existing listAudits query with status filter.
  const { data: rawAudits } = useWorkspaceQuery(api.audit_queries.listAudits, {
    status: ACTIVE_STATUSES,
  });

  const audits: AuditOption[] = (rawAudits ?? []).map((a) => ({
    id: a.id,
    displayName: a.displayName,
    code: a.code,
  }));

  const filteredAudits = query
    ? audits.filter(
        (a) =>
          a.displayName.toLowerCase().includes(query.toLowerCase()) ||
          a.code.toLowerCase().includes(query.toLowerCase()),
      )
    : audits;

  const handleSelect = (auditId: Id<"audits">) => {
    // Selecting an audit clears "No audit" flag
    if (explicitNoAudit) {
      onNoAuditChange(false);
    }
    // Toggle: clicking the already-selected audit deselects it
    if (value === auditId) {
      onChange(null);
    } else {
      onChange(auditId);
    }
    setQuery("");
    setOpen(false);
  };

  const handleSelectNoAudit = () => {
    // "No audit" is mutually exclusive with audit selection
    onNoAuditChange(!explicitNoAudit);
    if (!explicitNoAudit) {
      onChange(null);
    }
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      // Small delay to let the popover mount before focusing
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const selectedAudit = audits.find((a) => a.id === value) ?? null;

  return (
    <div className="mb-5">
      <label
        htmlFor="audit-context-select"
        className="text-[11px] font-semibold text-secondary uppercase tracking-[0.06em] mb-2 block"
      >
        {t("upload.auditSelect.label")}
      </label>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            ref={triggerRef}
            id="audit-context-select"
            type="button"
            className={`w-full min-h-[40px] rounded-lg border bg-card px-3 py-2 flex flex-wrap gap-1.5 items-center text-left transition-all duration-200 ${
              highlight
                ? "border-accent shadow-[0_0_0_3px_rgba(146,208,206,0.15)]"
                : "border-border hover:border-border/80"
            }`}
          >
            {explicitNoAudit ? (
              <span className="inline-flex items-center gap-1 rounded bg-muted/8 px-2 py-0.5 text-[12px] font-medium text-muted">
                <Ban size={12} className="shrink-0" />
                {t("upload.auditSelect.noAuditBadge")}
              </span>
            ) : selectedAudit ? (
              <span className="inline-flex items-center gap-1 rounded bg-accent/10 px-2 py-0.5 text-[12px] font-medium text-accent">
                <ClipboardList size={11} className="shrink-0" />
                {selectedAudit.displayName}
              </span>
            ) : (
              <span className="text-[13px] text-muted/80">
                {t("upload.auditSelect.placeholder")}
              </span>
            )}
            <ChevronDown size={13} className="ml-auto shrink-0 text-muted/70" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className="bg-card rounded-xl border border-border/80 shadow-lg z-50 w-[320px]"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* Search input */}
            <div className="p-2.5 border-b border-border/40 flex items-center gap-2">
              <Search size={13} className="text-muted/70 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("upload.auditSelect.searchPlaceholder")}
                className="w-full bg-transparent text-[13px] text-primary placeholder:text-muted/50 outline-none"
              />
            </div>

            {/* No audit option */}
            <div className="p-1 border-b border-border/40">
              <button
                type="button"
                onClick={handleSelectNoAudit}
                className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg transition-colors cursor-pointer text-left ${
                  explicitNoAudit
                    ? "bg-page text-primary"
                    : "text-primary/80 hover:text-primary hover:bg-page/60"
                }`}
              >
                <Ban size={13} className="shrink-0 text-muted/70" />
                <span className="flex-1">
                  {t("upload.auditSelect.noAuditOption")}
                </span>
                {explicitNoAudit && (
                  <Check size={13} className="shrink-0 text-accent" />
                )}
              </button>
            </div>

            {/* Options list */}
            {filteredAudits.length > 0 && (
              <div className="max-h-[240px] overflow-y-auto overscroll-contain p-1">
                {filteredAudits.map((audit) => {
                  const isSelected = value === audit.id;
                  return (
                    <button
                      key={audit.id}
                      type="button"
                      onClick={() => handleSelect(audit.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg transition-colors cursor-pointer text-left ${
                        isSelected
                          ? "bg-accent/10 text-primary"
                          : "text-primary/80 hover:text-primary hover:bg-page/60"
                      }`}
                    >
                      <ClipboardList
                        size={13}
                        className="shrink-0 text-muted/70"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="truncate block">
                          {audit.displayName}
                        </span>
                        {audit.code && (
                          <span className="text-[11px] text-muted/80">
                            {audit.code}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <Check size={13} className="shrink-0 text-accent" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {filteredAudits.length === 0 && (
              <div className="p-4 text-center text-[13px] text-muted/60">
                {query
                  ? t("upload.auditSelect.noResults")
                  : t("upload.auditSelect.noAudits")}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Info: when "no audit" selected, show that no matching will happen */}
      {explicitNoAudit && (
        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-muted/[0.06] border border-muted/15 px-3 py-2">
          <Info size={13} className="shrink-0 text-muted mt-0.5" />
          <div className="text-[12px] text-secondary leading-snug">
            {t("upload.auditSelect.noMatchingHint")}
          </div>
        </div>
      )}
    </div>
  );
}
